import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

// Counts from 0 to a target when scrolled into view; passes through non-numeric values
const CountUp = ({ value, duration = 1400 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const match = /^(\d+)(.*)$/.exec(value);
  const target = match ? parseInt(match[1], 10) : null;
  const suffix = match ? match[2] : "";
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView || target === null) return;
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setCount(Math.round(eased * target));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, target, duration]);

  return <span ref={ref}>{target === null ? value : `${count}${suffix}`}</span>;
};

const ease = [0.16, 1, 0.3, 1];
const heroFadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease, delay },
});
const stagger = (i) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-30px" },
  transition: { duration: 0.55, ease, delay: i * 0.09 },
});

const COMPANIES = ["Google", "Apple", "Microsoft", "Amazon", "Tesla", "LinkedIn", "PayPal", "Intuit"];

export default function HeroSection() {
  return (
    <>
      {/* ── HERO ── */}
      <section className="pt-40 pb-20 md:pt-48 md:pb-28 bg-white text-center overflow-hidden relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[8%] left-[8%] w-[400px] h-[400px] bg-brand/[0.06] rounded-full blur-[40px] animate-orb" />
          <div className="absolute top-[20%] right-[6%] w-[320px] h-[320px] bg-amber-300/[0.08] rounded-full blur-[40px] animate-orb-slow" />
          <div className="absolute bottom-[-60px] left-[25%] w-[500px] h-[180px] bg-brand/[0.04] rounded-full blur-[40px] animate-orb-med" />
        </div>
        <div className="absolute inset-0 hero-dots-light opacity-40 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <motion.div {...heroFadeUp(0)} className="flex justify-center mb-7">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-light text-brand text-xs font-medium uppercase tracking-widest border border-brand/20">
              <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
              Limited Slots Available
            </span>
          </motion.div>
          <motion.h1 {...heroFadeUp(0.08)} className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-tight text-ink mb-6">
            Strategic Tech Job Applications,
            <br />
            <span className="gradient-text-orange">Done for You.</span>
          </motion.h1>
          <motion.p {...heroFadeUp(0.16)} className="text-lg md:text-xl text-ink-secondary max-w-2xl mx-auto mb-10 leading-relaxed">
            We apply to relevant roles at top global tech companies on your behalf — so you can stay focused on interview prep, upskilling, and landing the role you deserve. For candidates in India and the US.
          </motion.p>
          <motion.div {...heroFadeUp(0.22)} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button asChild size="lg" className="hover:scale-[1.02] hover:shadow-[0_8px_32px_rgba(255,82,0,0.45)] shadow-sm">
              <Link to="/apply" data-testid="hero-apply-button">
                Apply to Work With Us <ArrowRight size={18} />
              </Link>
            </Button>
            <a href="https://cal.com" target="_blank" rel="noopener noreferrer" data-testid="hero-book-button" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-surface-raised text-ink font-medium hover:bg-[#E5E5EA] transition-all duration-300 text-base">
              Book a Strategy Call
            </a>
          </motion.div>
          <motion.div {...heroFadeUp(0.28)} className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
            {[{ c: "bg-green-500", t: "Official Portals Only" }, { c: "bg-brand", t: "Limited Slots" }, { c: "bg-blue-500", t: "Weekly Feedback" }].map((x) => (
              <div key={x.t} className="flex items-center gap-2 text-sm text-ink-secondary">
                <span className={`w-2 h-2 rounded-full ${x.c}`} />{x.t}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <section className="py-12 bg-surface-raised border-y border-black/5" data-testid="trust-strip">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-xs text-ink-secondary uppercase tracking-[0.2em] font-medium mb-7">Candidates targeting and placed at</p>
          <div className="relative overflow-hidden" style={{ maskImage: "linear-gradient(90deg, transparent, black 12%, black 88%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, black 12%, black 88%, transparent)" }}>
            <div className="flex w-max items-center gap-14 animate-marquee">
              {[...COMPANIES, ...COMPANIES].map((co, i) => (
                <span key={`${co}-${i}`} className="text-ink-secondary font-medium tracking-widest uppercase text-sm hover:text-ink transition-colors cursor-default whitespace-nowrap">{co}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS GRID ── */}
      <section className="py-20 bg-white" data-testid="stats-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: "40+", label: "Applications / Day", sub: "Per active candidate" },
              { value: "100%", label: "Official Portals", sub: "No spam tools, ever" },
              { value: "7 Days", label: "To First Applications", sub: "From onboarding" },
              { value: "Weekly", label: "Strategy Reviews", sub: "Built into every plan" },
            ].map((stat, i) => (
              <motion.div key={stat.label} {...stagger(i)}
                className="relative p-7 rounded-3xl bg-surface-raised hover:bg-white hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] hover:-translate-y-1 transition-all duration-400 text-center group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brand/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <p className="text-3xl font-display font-semibold text-brand mb-1 relative z-10"><CountUp value={stat.value} /></p>
                <p className="text-ink font-medium text-sm mb-1 relative z-10">{stat.label}</p>
                <p className="text-ink-secondary text-xs relative z-10">{stat.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
